import React, { useState } from 'react';
import { Bell, Check, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from './ui/sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

/**
 * Formulaire compact pour s'inscrire à la liste d'attente d'un produit "Bientôt disponible".
 * Affiché en remplacement du bouton "Ajouter au panier" quand product.coming_soon === true.
 */
const WaitlistForm = ({ productId, productName }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/products/${productId}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          phone: phone.trim()
        })
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Erreur lors de l'inscription");
      } else {
        setSubscribed(true);
        toast.success(data.message);
      }
    } catch (err) {
      toast.error('Erreur réseau, réessayez');
    } finally {
      setSubmitting(false);
    }
  };

  if (subscribed) {
    return (
      <div
        data-testid="waitlist-success"
        className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 flex items-start gap-3"
      >
        <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-emerald-300 font-semibold text-sm">Inscription confirmée</p>
          <p className="text-emerald-200/80 text-xs mt-1">
            Vous recevrez un email dès que <strong>{productName}</strong> sera disponible.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="waitlist-form"
      className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <Bell className="w-4 h-4 text-amber-400" />
        <h4 className="text-sm font-semibold text-white">Être prévenu de la disponibilité</h4>
      </div>
      <p className="text-xs text-white/60">
        Laissez-nous votre email et nous vous préviendrons en exclusivité dès la commercialisation de ce produit.
      </p>
      <div className="space-y-2">
        <Input
          type="text"
          placeholder="Votre prénom (optionnel)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-zinc-900 border-zinc-800 text-white placeholder:text-white/40"
          data-testid="waitlist-name-input"
        />
        <Input
          type="email"
          placeholder="votre@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-zinc-900 border-zinc-800 text-white placeholder:text-white/40"
          data-testid="waitlist-email-input"
        />
        <Input
          type="tel"
          placeholder="Téléphone (optionnel) — pour un appel privilégié"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="bg-zinc-900 border-zinc-800 text-white placeholder:text-white/40"
          data-testid="waitlist-phone-input"
        />
      </div>
      <Button
        type="submit"
        disabled={submitting || !email.trim()}
        className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
        data-testid="waitlist-submit-btn"
      >
        <Mail className="w-4 h-4 mr-2" />
        {submitting ? 'Inscription…' : 'Me prévenir à la sortie'}
      </Button>
    </form>
  );
};

export default WaitlistForm;
